import React, { FC, useEffect, useRef } from 'react';
import {
    FlatList,
    Modal,
    TouchableOpacity,
    View,
    useWindowDimensions,
} from 'react-native';
import { useTheme } from '@rneui/themed';
import { ZenDivider, ZenEmptyComponent, ZenIcon, ZenText } from 'zen-core-elements';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Shape of a single sort option. */
export interface SortOption {
    /** Unique identifier – used to track the active selection. */
    id?: number;
    /** Human-readable label shown in the list. */
    name?: string;
    /** Human-readable label shown in the list (alternative to name). */
    label?: string;
    /** Database column / field name sent to the API. */
    field?: string;
    /** Database column name sent to the API (alternative to field). */
    column?: string;

    key?: string;
    selected?: boolean;
    /** Sort direction sent to the API. */
    order?: 'ASC' | 'DESC';
}

export interface CommonSortModalProps {
    visible: boolean;
    onClose: () => void;
    options: SortOption[];
    selectedId?: number | null;
    onSelect: (option: SortOption) => void;
    title?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CommonSortModal: FC<CommonSortModalProps> = ({
    visible,
    onClose,
    options,
    selectedId,
    onSelect,
    title = 'Sort By',
}) => {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;
    const scrollRef = useRef<FlatList>(null);
    const { theme } = useTheme()

    // When the modal opens scroll to the selected item; reset search on close.
    useEffect(() => {
        const idx = options?.findIndex(i => i?.id === selectedId);

        if (idx > 0) {
            setTimeout(() => {
                scrollRef?.current?.scrollToIndex({ index: idx, animated: true });
            }, 400);
        }
    }, [visible]);

    const renderItem = ({ item, index }: { item: SortOption, index: number }) => {
        const isSelected = selectedId !== null && item.id === selectedId;
        return (
            <TouchableOpacity
                key={index}
                activeOpacity={0.7}
                onPress={() => onSelect(item)}
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingVertical: 14,
                    paddingHorizontal: 12,
                    marginBottom: 10,
                    borderRadius: 12,
                    backgroundColor: isSelected
                        ? theme?.lightBackgroundColor
                        : theme?.light,
                    borderWidth: 1,
                    borderColor: isSelected
                        ? theme?.primary
                        : theme?.borderColor,
                }}
            >
                <ZenText
                    content={item.name || item.label}
                    color={isSelected ? theme?.primary : theme?.lightDark}
                    size={theme?.smallFontSize}
                    bold={isSelected}
                />

                {isSelected && (
                    <View
                        style={{
                            width: 24,
                            height: 24,
                            borderRadius: 12,
                            backgroundColor: theme?.primary,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <ZenIcon
                            name="check"
                            type="material-community"
                            size={15}
                            color={theme?.light}
                        />
                    </View>
                )}
            </TouchableOpacity>
        );
    }

    return (
        <Modal
            visible={visible}
            onRequestClose={onClose}
            transparent
            animationType="fade"
            statusBarTranslucent
            supportedOrientations={[
                'portrait',
                'landscape',
                'landscape-left',
                'landscape-right',
            ]}
        >
            {/* ── Backdrop ──────────────────────────────────────────── */}
            <TouchableOpacity
                activeOpacity={1}
                onPress={onClose}
                style={{
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                }}
            >
                {/* ── Card (swallow taps so backdrop doesn't fire) ───── */}
                <TouchableOpacity
                    activeOpacity={1}
                    onPress={() => {/* prevent backdrop close */ }}
                    style={{
                        backgroundColor: theme?.light,
                        borderRadius: 16,
                        padding: 18,
                        paddingBottom: 10,
                        width: isLandscape ? 380 : '85%',
                    }}
                >
                    {/* ── Header row ────────────────────────────────────── */}
                    <View
                        style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <ZenText
                            content={title}
                            size={theme?.mediumFontSize}
                            color={theme?.dark}
                            bold
                        />

                        <TouchableOpacity
                            onPress={onClose}
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 16,
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: theme?.lightBackgroundColor,
                            }}
                        >
                            <ZenIcon
                                name="close"
                                type="material-community"
                                size={20}
                                color={theme?.dark}
                            />
                        </TouchableOpacity>
                    </View>
                    <ZenDivider style={{ marginVertical: 10, backgroundColor: theme?.borderColor }} />
                    {/* ── List ─────────────────────────────────────── */}
                    <FlatList
                        ref={scrollRef}
                        data={options}
                        renderItem={renderItem}
                        style={{ maxHeight: isLandscape ? 260 : 380 }}
                        keyExtractor={(_, index) => index?.toString()}
                        contentContainerStyle={{ paddingBottom: 20, flexGrow: 1, }}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                        ItemSeparatorComponent={() => <View style={{ height: 12 }} />}
                        ListEmptyComponent={
                            <ZenEmptyComponent viewStyle={{ flex: 1, justifyContent: 'center', alignItems: 'center' }} />
                        }
                        onScrollToIndexFailed={(info) => {
                            setTimeout(() => {
                                scrollRef.current?.scrollToIndex({
                                    index: info.index,
                                    animated: true,
                                });
                            }, 100);
                        }}
                    />
                </TouchableOpacity>
            </TouchableOpacity>
        </Modal>
    );
};

export default CommonSortModal;
